import type { Rarity } from './parts/partTypes';

/**
 * The Smelter — Sir Cumference's unlock (MINIGAME_PROPOSALS O6). Queue spare
 * parts; they melt over time into Alloy, which buys permanent, chunky,
 * ever-pricier CAPACITY upgrades (+Weight, +Storage, +drone bay slots).
 *
 * Design call: ADDITIVE on purpose. Capacities gate options, not throughput,
 * so they are the correct additive home. This is also where late-game
 * inventory pressure goes to die (the bulk-scrap QoL lives here).
 */

/** Alloy yielded per melted part, by rarity (+1 per level invested past 1).
 *  Melting a forged-tier part is legal and lucrative, if emotionally costly. */
export const ALLOY_YIELD: Record<Rarity, number> = {
  common: 1,
  uncommon: 3,
  rare: 8,
  epic: 20,
  legendary: 50,
  unit: 125,
  goated: 300,
};

/** One part melts every this-many ms. */
export const SMELT_MS = 40_000;

export interface SmeltUpgradeDef {
  id: string;
  name: string;
  flavor: string;
  /** What one rank adds (display). */
  effectLabel: string;
  baseCost: number;
  /** cost(rank) = baseCost × growth^rank */
  costGrowth: number;
  /** 0 = unlimited ranks. */
  maxRank: number;
}

export const WEIGHT_PER_RANK = 4;
export const STORAGE_PER_RANK = 10;
export const DRONES_PER_RANK = 1;

export const SMELT_UPGRADES: SmeltUpgradeDef[] = [
  {
    id: 'su.ballast',
    name: 'Ballast Plating',
    flavor: 'Dense alloy ribs. Your chassis stops complaining about heavy builds.',
    effectLabel: `+${WEIGHT_PER_RANK} Weight capacity`,
    baseCost: 25,
    costGrowth: 2,
    maxRank: 0,
  },
  {
    id: 'su.racks',
    name: 'Stash Racks',
    flavor: 'Shelving forged from former enemies. They would have wanted this.',
    effectLabel: `+${STORAGE_PER_RANK} part Storage`,
    baseCost: 20,
    costGrowth: 2,
    maxRank: 0,
  },
  {
    id: 'su.bay',
    name: 'Bay Extension',
    flavor: 'One more kennel for one more drone. The bay smells of solder.',
    effectLabel: `+${DRONES_PER_RANK} max drone`,
    baseCost: 40,
    costGrowth: 2,
    maxRank: 6,
  },
];

export function getSmeltUpgrade(id: string): SmeltUpgradeDef | undefined {
  return SMELT_UPGRADES.find((u) => u.id === id);
}
